import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { LoadingSpinner } from '@/shared/ui/components'
import type { LoadOrgStatus } from '@/modules/auth/use-cases'
import { COMMAND_CENTER_CAPABILITIES } from '../components'
import { useLandingPage } from './hooks/use-landing-page'

export type LandingPageProps = {
  onLoadOrgStatus: LoadOrgStatus
}

function Backdrop() {
  return (
    <>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/auth-background.jpg')" }}
        aria-hidden
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-sidebar via-sidebar/70 to-sidebar"
        aria-hidden
      />
    </>
  )
}

export function LandingPage({ onLoadOrgStatus }: LandingPageProps) {
  const vm = useLandingPage({ onLoadOrgStatus })

  if (vm.status === 'loading' || vm.status === 'redirecting') {
    return (
      <div className="relative min-h-screen bg-sidebar font-marketing">
        <Backdrop />
        <div
          data-testid="landing-loading"
          className="relative z-10 flex min-h-screen items-center justify-center"
        >
          <LoadingSpinner size="lg" color="white" />
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-sidebar font-marketing">
      <Backdrop />
      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="flex items-center justify-between px-6 py-6 sm:px-10">
          <img
            src="/blindbook-logo.svg"
            alt="BlindBook"
            width={120}
            height={40}
            className="h-9 w-auto brightness-0 invert sm:h-10"
          />
          <a
            href="https://blindbook.com"
            className="inline-flex items-center gap-1 text-sm text-fg-dim transition-colors hover:text-foreground"
          >
            blindbook.com
            <ArrowUpRight className="size-3.5" aria-hidden />
          </a>
        </header>

        <main className="flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
          <div className="w-full max-w-6xl">
            <p className="text-center text-2xs font-semibold uppercase tracking-widest text-blaze">
              Command Center
            </p>
            <h1 className="mx-auto mt-4 max-w-3xl text-center text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Run the whole operation
              <br className="hidden sm:inline" /> from one dashboard.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-center text-base leading-relaxed text-fg-2 sm:text-lg">
              BlindBook&trade; replaces the spreadsheets, group texts, and guesswork — so you can
              focus on what matters: the hunt.
            </p>

            <div
              data-testid="cta-grid"
              className="mx-auto mt-9 flex w-full max-w-md flex-col gap-3 sm:flex-row"
            >
              <Link
                to="/sign-in"
                className="flex-1 rounded-md bg-blaze px-6 py-3.5 text-center font-semibold text-blaze-ink transition-opacity hover:opacity-90"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="flex-1 rounded-md border border-border-strong px-6 py-3.5 text-center font-semibold text-foreground transition-colors hover:bg-hover"
              >
                Register
              </Link>
            </div>
            <p className="mt-4 text-center text-sm text-fg-dim">
              Already part of an outfitter, club, or crew? Sign in. Setting one up? Register your
              operation.
            </p>

            <div
              data-testid="feature-pills-grid"
              className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-x-12 lg:grid-cols-4 lg:gap-x-10"
            >
              {COMMAND_CENTER_CAPABILITIES.map((capability) => (
                <div key={capability.key} className="flex flex-col items-center text-center">
                  <span className="flex size-12 items-center justify-center rounded-lg bg-blaze-soft ring-1 ring-blaze/25">
                    {capability.icon}
                  </span>
                  <h2 className="mt-4 text-lg font-semibold text-foreground">
                    {capability.label}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-fg-dim">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </main>

        <footer className="px-6 pb-8 text-center text-xs text-fg-dim sm:px-10">
          &copy; {new Date().getFullYear()} WaterfowlAI · BlindBook&trade; — The World&apos;s First
          Hunting Operating System
        </footer>
      </div>
    </div>
  )
}
